import { useEffect } from 'react';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import {
  restoreSession,
  getCurrentUser,
  completeAuthBootstrap,
  selectAuthBootstrapComplete,
} from '../store/features/auth';
import { refreshTokenOnce } from '../services/authBootstrap';
import { tokenStore } from '../services/tokenStore';

export interface UseAuthInitReturn {
  /** @deprecated Public routes no longer block on this; use selectAuthBootstrapComplete in guards. */
  isInitializing: boolean;
  isReady: boolean;
}

/**
 * Starts silent session restore in the background.
 * Public pages render immediately; ProtectedRoute waits on bootstrapComplete.
 */
export const useAuthInit = (): UseAuthInitReturn => {
  const dispatch = useAppDispatch();
  const bootstrapComplete = useAppSelector(selectAuthBootstrapComplete);

  useEffect(() => {
    if (bootstrapComplete) return;

    let cancelled = false;

    const initAuth = async () => {
      try {
        dispatch(restoreSession());

        let hasToken = !!tokenStore.getAccessToken();
        if (!hasToken) {
          await refreshTokenOnce(dispatch);
          hasToken = !!tokenStore.getAccessToken();
        }

        if (hasToken) {
          await dispatch(getCurrentUser());
        }
      } catch {
        // Route guards handle unauthenticated users after bootstrap.
      } finally {
        if (!cancelled) {
          dispatch(completeAuthBootstrap());
        }
      }
    };

    void initAuth();

    return () => {
      cancelled = true;
    };
  }, [dispatch, bootstrapComplete]);

  return {
    isInitializing: !bootstrapComplete,
    isReady: bootstrapComplete,
  };
};
